import { Link } from "@tanstack/react-router";
import { KeyRound, MoreHorizontal, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type User, useUsers } from "@/hooks/use-users";
import { useSession } from "@/lib/auth-client";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

type DialogState =
  | { type: "none" }
  | { type: "resetPassword"; user: User }
  | { type: "delete"; user: User };

// In production the tab is a read-only list: sign-in is Microsoft only, so a
// password reset does nothing, and access is managed on Settings -> Access.
// Outside production the row actions stay for working with the seeded accounts.
const showRowActions = process.env.NODE_ENV !== "production";

export function UsersTable() {
  const { data: session } = useSession();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  const {
    data: users = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useUsers({ search: debouncedSearch });

  function closeDialog() {
    setDialog({ type: "none" });
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load users"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      {/* Accounts appear here on first sign-in. Who may sign in, and whether
          they are an admin, is the access grant's job, not this tab's. */}
      <p className="text-sm text-muted-foreground">
        To let someone in or change what they can see, use{" "}
        <Link to="/dashboard/settings/access" className="font-medium text-primary hover:underline">
          Settings → Access
        </Link>
        .
      </p>
      <SearchInput
        value={search}
        onValueChange={setSearch}
        placeholder="Search by email..."
        className="max-w-sm"
      />

      <DataTable
        headers={[
          { label: "Name" },
          { label: "Email" },
          { label: "Role" },
          { label: "Created", className: "hidden md:table-cell" },
          ...(showRowActions ? [{ label: "Actions", className: "text-right" }] : []),
        ]}
        loading={isLoading}
        empty={
          <EmptyState icon={Users} title="No users found" description="Try a different search." />
        }
      >
        {users.map((user) => (
          <tr
            key={user.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">{user.name}</td>
            <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
            <td className="px-4 py-3">
              <span
                className={
                  user.role === "admin"
                    ? "inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                    : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                }
              >
                {user.role}
              </span>
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {new Date(user.createdAt).toLocaleDateString()}
            </td>
            {showRowActions && (
              <td className="px-4 py-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" disabled={user.id === session?.user?.id}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setDialog({ type: "resetPassword", user })}>
                      <KeyRound className="h-4 w-4" />
                      Reset Password
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDialog({ type: "delete", user })}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete User
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            )}
          </tr>
        ))}
      </DataTable>

      {dialog.type === "resetPassword" && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteUserDialog open onOpenChange={(open) => !open && closeDialog()} user={dialog.user} />
      )}
    </div>
  );
}
